import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';

/**
 * Custom hook for WebSocket connection with real-time crawl updates
 * Handles connection, reconnection, and message routing
 */
export const useWebSocket = (onMessage) => {
  const { user } = useAuth();
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const subscribedPlaces = useRef(new Set());

  const getWebSocketUrl = useCallback(() => {
    // Use environment variable instead of window.location
    return `${WS_BASE_URL}/ws/crawl-updates/`;
  }, []);

  const connect = useCallback(() => {
    if (!user) {
      // console.log('No user authenticated, skipping WebSocket connection');
      return;
    }

    if (ws.current?.readyState === WebSocket.OPEN) {
      // console.log('WebSocket already connected');
      return;
    }

    try {
      const wsUrl = getWebSocketUrl();
      // console.log('Connecting to WebSocket:', wsUrl);
      
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        // console.log('✅ WebSocket connected successfully');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;

        // Re-subscribe to places after reconnection
        if (subscribedPlaces.current.size > 0) {
          // console.log(`Re-subscribing to ${subscribedPlaces.current.size} places`);
          subscribedPlaces.current.forEach(placeId => {
            ws.current.send(JSON.stringify({
              action: 'subscribe_place',
              place_id: placeId
            }));
          });
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log('📨 WebSocket message:', data.type, data);
          
          if (onMessage) {
            onMessage(data);
          }
        } catch (error) {
          // console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setConnectionError('WebSocket connection error');
      };

      ws.current.onclose = (event) => {
        // console.log('🔌 WebSocket closed:', event.code, event.reason);
        setIsConnected(false);

        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          // console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeout.current = setTimeout(() => {
            reconnectAttempts.current += 1;
            connect();
          }, delay);
        } else {
          console.error('❌ Max reconnection attempts reached');
          setConnectionError('Unable to connect to real-time updates');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setConnectionError('Failed to create WebSocket connection');
    }
  }, [user, getWebSocketUrl, onMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }

    setIsConnected(false);
    subscribedPlaces.current.clear();
  }, []);

  const send = useCallback((data) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(data));
      // console.log('📤 Sent WebSocket message:');
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message');
    }
  }, []);

  const subscribePlaces = useCallback((placeIds) => {
    if (!Array.isArray(placeIds)) {
      placeIds = [placeIds];
    }

    placeIds.forEach(placeId => {
      subscribedPlaces.current.add(placeId);
      send({ action: 'subscribe_place', place_id: placeId });
    });
    
    // console.log('📍 Subscribed to places:');
  }, [send]);

  const unsubscribePlaces = useCallback((placeIds) => {
    if (!Array.isArray(placeIds)) {
      placeIds = [placeIds];
    }

    placeIds.forEach(placeId => {
      subscribedPlaces.current.delete(placeId);
      send({ action: 'unsubscribe_place', place_id: placeId });
    });
    
    // console.log('📍 Unsubscribed from places:');
  }, [send]);

  // Ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      send({ action: 'ping', timestamp: Date.now() });
    }, 30000); // Ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, [isConnected, send]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionError,
    send,
    subscribePlaces,
    unsubscribePlaces,
    reconnect: connect
  };
};

export default useWebSocket;